import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DrawerComponent, PaginationConfig, QueryParams } from '@shared';
import { PayoutHistory, UserService } from '@shared/services';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { firstValueFrom } from 'rxjs';
import { InstructorPaymentMethod, InstructorUserDetails, ModalComponent, ModalService, PaginationService } from 'thkee-common';
import { ProcessingDirective } from '../../../../../../../thkee-common/src/lib/directives/processing.directive';
import { CommonPaginationComponent } from '../../../../@shared/components/common-pagination/common-pagination.component';
import { DrawerComponent as DrawerComponent_1 } from '../../../../@shared/components/drawer/drawer.component';
const pageSize: number = 10;
@Component({
  selector: 'app-activation-details',
  templateUrl: './activation-details.component.html',
  styleUrls: ['./activation-details.component.scss'],
  imports: [
    ProcessingDirective,
    AngularSvgIconModule,
    NgTemplateOutlet,
    CommonPaginationComponent,
    DrawerComponent_1,
    ModalComponent,
    FormsModule,
    DatePipe,
  ],
})
export class ActivationDetailsComponent implements OnInit {
  private userService = inject(UserService);
  private activatedRoute = inject(ActivatedRoute);
  private readonly modalService = inject(ModalService);
  private router = inject(Router);
  private paginationService = inject(PaginationService);

  isLoading: boolean = false;
  payoutInstructorProfileDetails!: InstructorUserDetails;
  instructorId: string = '';
  params: string = '';
  tabs: { label: string; value: string }[] = [
    { label: 'Profile', value: 'profile' },
    { label: 'Identity Verification', value: 'identity' },
    { label: 'Terms Of Use', value: 'terms' },
    { label: 'Marketing', value: 'marketing' },
    { label: 'Payout Method', value: 'payout' },
  ];

  title1: string = $localize`Thkee’s Promotional Programs`;
  content11: string = $localize`Thkee offers several optional marketing programs in which you can choose to participate. These programs can help increase your revenue potential on Thkee by finding the optimal price point for your content and promoting it through additional marketing channels.`;
  content12: string = $localize`There is no up-front cost to participate in these programs, and you can modify your participation status at any time. However, changes you make will not apply to currently active campaigns.`;
  content13: string = $localize`These programs are subject to the terms of our [Promotions Policy], so you should read it carefully.`;

  title2: string = $localize`Deals Program`;
  content21: string = $localize`We will take care of promoting your content to a huge range of audiences using our advanced marketing tools. Participation can increase your revenue potential by enabling Thkee to offer your content at a compelling discount via targeted promotions.  `;
  content22: string = $localize`The Deals Program allows Thkee to offer your content for a price no lower than 75 SR (or local equivalent) except in select countries or to certain resellers. It also allows Thkee to optimize your list price in different markets.`;
  content23: string = $localize`The Deals Program allows Thkee to offer your content for a price no lower than 75 SR (or local equivalent) except in select countries or to certain resellers. It also allows Thkee to optimize your list price in different markets.`;

  // GET Payment method details
  paypalMethod?: InstructorPaymentMethod;
  payoneerMethod?: InstructorPaymentMethod;
  bankMethod: InstructorPaymentMethod[] = [];
  queryParams: QueryParams = { page: 1, page_size: pageSize };
  isLoadingHistory: boolean = false;
  payoutHistory: PayoutHistory[] = [];
  paginationConfig: PaginationConfig = {
    pageCountSummary: false,
    pageSize: true,
    firstAndLast: true,
  };

  adminPayoutAction: 'loaded' | 'loading' | 'failed' = 'loading';

  ngOnInit(): void {
    this.params = this.activatedRoute.snapshot.queryParams['params'];
    this.instructorId = this.activatedRoute.snapshot.params['instructorId'];
    this.getPayoutInstructorProfileDetails(this.instructorId);
    this.selectedTab = this.params;
    this.queryUpdater();
  }

  private getPayoutInstructorProfileDetails(instructorId: string) {
    this.isLoading = true;
    this.userService.getPayoutInstructorProfileDetails(instructorId).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.payoutInstructorProfileDetails = res;
        this.bankMethod = res.payment_methods.filter((m: InstructorPaymentMethod) => m.name === 'bank_account');
        this.paypalMethod = res.payment_methods.find((m: InstructorPaymentMethod) => m.name === 'paypal');
        this.payoneerMethod = res.payment_methods.find((m: InstructorPaymentMethod) => m.name === 'payoneer');
        this.adminPayoutAction = 'loaded';
      },
      error: (err) => {},
    });
  }

  // Update query
  private queryUpdater() {
    firstValueFrom(this.activatedRoute.queryParams).then((params) => {
      this.queryParams.page = params['page'] ? +params['page'] : 1;
      this.queryParams.page_size = params['page_size'] ? +params['page_size'] : 10;
      this.getAdminInstructorPayoutHistory(this.instructorId);
    });
  }

  private getAdminInstructorPayoutHistory(instructorId: string) {
    this.isLoadingHistory = true;
    this.userService.getAdminInstructorPayoutHistory(instructorId, this.queryParams).subscribe({
      next: (res) => {
        this.isLoadingHistory = false;
        this.payoutHistory = res.results;
        this.paginationService.getPager(res.count, this.queryParams.page, this.queryParams.page_size);
      },
      error: (err) => {
        this.isLoadingHistory = false;
      },
    });
  }

  // Pagination
  paginationAction(value: number) {
    this.queryParams.page = value;
    this.getAdminInstructorPayoutHistory(this.instructorId);
  }

  pageSizeAction(value: number) {
    this.queryParams.page_size = value;
    this.getAdminInstructorPayoutHistory(this.instructorId);
  }

  // Disable
  @ViewChild('disableConfirmedModal') private disableConfirmedModal!: ModalComponent;
  disableReason: string = '';
  disablePayout() {
    this.disableConfirmedModal.open();
  }

  disablePayoutConfirm() {
    this.adminPayoutAction = 'loading';
    this.userService.adminDisablePayout(this.instructorId, { reason: this.disableReason }).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
        this.disableReason = '';
        this.disableConfirmedModal.close();
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Active payout
  activePayout() {
    this.modalService.confirm({
      title: 'Active Confirmation',
      message: `Are you sure, you want to active this payout ?`,
      onConfirm: () => {
        this.activePayoutConfirm();
      },
    });
  }

  private activePayoutConfirm() {
    this.adminPayoutAction = 'loading';
    this.userService.adminActivePayout(this.instructorId).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Verify
  verifyInstructor() {
    this.modalService.confirm({
      title: 'Verify Confirmation',
      message: `Are you sure, you want to verify this instructor ?`,
      onConfirm: () => {
        this.verifyPayoutConfirm();
      },
    });
  }

  private verifyPayoutConfirm() {
    this.adminPayoutAction = 'loading';
    this.userService.adminVerifyInstructorProfile(this.instructorId).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Revoke
  revokeVerification() {
    this.modalService.confirm({
      title: 'Verify Confirmation',
      message: `Are you sure, you want to verify this instructor ?`,
      onConfirm: () => {
        this.revokePayoutConfirm();
      },
    });
  }

  private revokePayoutConfirm() {
    this.adminPayoutAction = 'loading';
    this.userService.adminRejectedInstructorProfile(this.instructorId).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Approved
  approved(paymentMethodId: number) {
    this.accountDetails.close();
    this.adminPayoutAction = 'loading';
    this.userService.adminApprovedAccount(paymentMethodId?.toString()).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Rejected
  rejectedReason: string = '';
  paymentMethodId!: number;
  @ViewChild('rejectConfirmedModal') private rejectConfirmedModal!: ModalComponent;
  rejected(paymentMethodId: number) {
    this.paymentMethodId = paymentMethodId;
    this.rejectConfirmedModal.open();
  }

  rejectedConfirmed() {
    this.rejectConfirmedModal.close();
    this.adminPayoutAction = 'loading';
    this.userService.adminRejectAccount(this.paymentMethodId?.toString(), { reason: this.rejectedReason }).subscribe({
      next: (res) => {
        this.getPayoutInstructorProfileDetails(this.instructorId);
      },
      error: (err) => {
        this.adminPayoutAction = 'failed';
      },
    });
  }

  // Drawer
  bankAccount?: InstructorPaymentMethod;
  @ViewChild('accountDetails') private accountDetails!: DrawerComponent;
  showDetails(account: InstructorPaymentMethod) {
    this.bankAccount = account;
    this.accountDetails.open();
  }
  // Tab
  selectedTab: string = 'profile';
  selectTab(item: string) {
    this.selectedTab = item;
    this.router.navigate([`/users/payout-activation/details/${this.instructorId}`], {
      queryParams: { params: item },
      replaceUrl: true,
    });
  }
}
